import { useEffect, useState } from "react";
import type React from "react";
import { Link, useParams } from "react-router-dom";
import { canonicalizeHandle, InvalidHandleError } from "@boon/normalize";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { X402ActivityRow } from "../components/x402/X402ActivityRow";
import { X402ReviewerReviewList } from "../components/x402/X402ReviewerReviewList";
import {
  fetchAgentMetadata,
  fetchPublicProfile,
  fetchProfile,
  formatRelative,
  formatUsdc,
  shortAddr,
  type AgentMetadataResponse,
  type PublicIdentityProfile,
  type ProfileResponse,
} from "../lib/api";
import { fetchX402ReviewerReviews, type X402ReviewerReviewItem } from "../lib/x402Discovery";
import {
  baseAddressFromAccountId,
  fetchPublicX402ProfileByWallet,
  toBaseAccountId,
  type X402ProfileActivity,
  type X402PublicProfileResponse,
} from "../lib/x402Profile";

/*
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5
 * Hallmark · macrostructure: Almanac · theme: boon warm paper
 *
 * Three full-width bands:
 *   1. Masthead — handle + lede
 *   2. Stats strap — 4 metrics, horizontal, full-width edge
 *   3. Profile details — wide ledger, full-width
 *
 * Per-boon listing is intentionally absent — that data lives behind the
 * x402-gated graph API. Individual boons are reachable via /b/:txHash.
 */
export function ProfilePage() {
  const { handle = "" } = useParams();
  const decodedHandle = decodeURIComponent(handle);
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [agent, setAgent] = useState<AgentMetadataResponse | null>(null);
  const [publicProfile, setPublicProfile] = useState<PublicIdentityProfile | null>(null);
  const [x402Reviews, setX402Reviews] = useState<X402ReviewerReviewItem[] | null>(null);
  const [x402WalletProfile, setX402WalletProfile] = useState<X402PublicProfileResponse | null>(null);
  const [x402ProfileStatus, setX402ProfileStatus] = useState<"idle" | "loading" | "available" | "not_found" | "unavailable">("idle");
  const [x402Loading, setX402Loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const canonical = (() => {
    try {
      return canonicalizeHandle(decodedHandle);
    } catch (err) {
      return err instanceof InvalidHandleError ? null : null;
    }
  })();
  const isAgentProfile = canonical?.scheme === "agent";
  const metadata = sanitizeAgentMetadata(agent?.metadata ?? null);
  const profileName = publicProfile?.displayName ?? metadata?.name ?? (decodedHandle || "Unknown handle");
  const profileDescription = publicProfile?.description ?? metadata?.description;
  const agentHasNoBoons = Boolean(isAgentProfile && data && data.boonsReceived === 0);
  const x402Activity = x402WalletProfile ? identityActivityFacts(x402WalletProfile, x402Reviews) : [];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAgent(null);
    setPublicProfile(null);
    setX402Reviews(null);
    setX402WalletProfile(null);
    setX402ProfileStatus("idle");
    setX402Loading(false);
    const loadAgent =
      canonical?.scheme === "agent"
        ? fetchAgentMetadata(canonical.username).catch(() => null)
        : Promise.resolve(null);
    const loadPublicProfile =
      canonical && profileLookupSupported(canonical.scheme)
        ? loadIdentityProfile(canonical)
        : Promise.resolve(null);
    Promise.all([fetchProfile(decodedHandle), loadAgent, loadPublicProfile])
      .then(([profile, agentMetadata, identityProfile]) => {
        if (!cancelled) {
          setData(profile);
          setAgent(agentMetadata);
          setPublicProfile(identityProfile);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [decodedHandle, canonical?.scheme, canonical?.username]);

  useEffect(() => {
    let cancelled = false;
    const wallet = data?.linkedWallet;
    if (!wallet) {
      setX402Reviews(null);
      setX402WalletProfile(null);
      setX402ProfileStatus("idle");
      setX402Loading(false);
      return () => { cancelled = true; };
    }
    setX402Loading(true);
    setX402ProfileStatus("loading");
    async function loadX402ProfileSignals() {
      let walletProfile: X402PublicProfileResponse | null = null;
      let profileStatus: "available" | "not_found" | "unavailable" = "unavailable";
      try {
        walletProfile = await fetchPublicX402ProfileByWallet(toBaseAccountId(wallet!));
        profileStatus = "available";
      } catch (cause) {
        profileStatus = cause instanceof Error && /returned 404(?:\D|$)/.test(cause.message) ? "not_found" : "unavailable";
      }
      const reviewerWallets = walletProfile
        ? walletProfile.profile.wallets.flatMap((entry) => {
            const address = baseAddressFromAccountId(entry.accountId);
            return address ? [address] : [];
          })
        : [wallet!];
      const pages = await Promise.allSettled(reviewerWallets.map((address) => fetchX402ReviewerReviews(address, 10)));
      const items = pages.flatMap((result) => result.status === "fulfilled" ? result.value.items : []);
      if (!cancelled) {
        setX402WalletProfile(walletProfile);
        setX402ProfileStatus(profileStatus);
        setX402Reviews(dedupeReviewerItems(items).slice(0, 3));
        setX402Loading(false);
      }
    }
    void loadX402ProfileSignals();
    return () => { cancelled = true; };
  }, [data?.linkedWallet]);

  return (
    <>
      <Nav current="board" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-8 md:pt-16 max-w-6xl mx-auto">
          <div className="mb-10 flex items-start justify-between gap-6 animate-fade-up">
            <header className="space-y-3 min-w-0">
            <p className="text-sm text-muted btn-mono tracking-wide uppercase">
              Boon profile
            </p>
            <h1 className="text-4xl md:text-6xl font-display tracking-tight leading-[0.98] break-all">
              {profileName}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              {canonical && (
                <span className="pill pill-faint">
                  {canonical.handle}
                </span>
              )}
              {publicProfile?.url && (
                <a
                  href={publicProfile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pill pill-olive hover:underline"
                >
                  {providerProfileLinkLabel(publicProfile.provider)} ↗
                </a>
              )}
            </div>
            {isAgentProfile && agentHasNoBoons && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="pill pill-faint border-danger/30 text-danger">
                  Unverified — no Boons received yet
                </span>
              </div>
            )}
            <p className="text-lg text-ink-soft leading-relaxed max-w-2xl">
              {profileDescription ??
                "Aggregate-only public reputation. Individual boon discovery lives on receipts or the paid graph API."}
            </p>
            <div className="pt-2">
              <Link
                to={`/send?handle=${encodeURIComponent(canonical?.handle ?? decodedHandle)}`}
                className="btn btn-primary whitespace-nowrap"
              >
                Send a Boon
                <span aria-hidden="true">→</span>
              </Link>
            </div>
            </header>
            {publicProfile?.avatarUrl && (
              <img
                src={publicProfile.avatarUrl}
                alt=""
                loading="lazy"
                className="h-20 w-20 md:h-24 md:w-24 shrink-0 rounded-xl border border-faint bg-paper-deep object-cover"
              />
            )}
          </div>
        </section>

        {loading && (
          <div className="px-6 md:px-10 max-w-6xl mx-auto">
            <Panel message="Loading recognition…" />
          </div>
        )}
        {!loading && error && (
          <div className="px-6 md:px-10 max-w-6xl mx-auto">
            <Panel message="Could not load this profile." sub={error} />
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.linkedWallet && (
              <section
                className="px-6 md:px-10 max-w-6xl mx-auto animate-fade-up"
                style={{ animationDelay: "100ms" }}
                aria-labelledby="identity-x402-heading"
              >
                <div className="border-y border-faint py-7">
                  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-3xl">
                      <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Linked wallet activity</p>
                      <h2 id="identity-x402-heading" className="mt-2 text-3xl font-display tracking-tight">x402 usage and recognition</h2>
                      <p className="mt-2 text-sm text-muted leading-relaxed">
                        {x402Loading || x402ProfileStatus === "idle"
                          ? "Checking this identity’s linked wallet for public x402 activity…"
                          : x402WalletProfile
                            ? "This Boon identity is connected to a public profile of separately controlled x402 wallets."
                            : x402ProfileStatus === "not_found"
                              ? "This identity’s linked wallet has not published a public x402 wallet profile."
                              : "Boon could not check this wallet’s public x402 profile. Its review history remains available below when the network responds."}
                      </p>
                    </div>
                    <Link
                      className="min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep hover:underline"
                      to={x402WalletProfile ? `/x402/profiles/${x402WalletProfile.profile.id}` : `/x402/reviewers/${data.linkedWallet}`}
                    >
                      {x402WalletProfile ? "Open full x402 profile →" : "View wallet review history →"}
                    </Link>
                  </div>

                  {x402WalletProfile && (
                    <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="Public x402 profile summary">
                      <X402SummaryFact value={x402WalletProfile.profile.wallets.length} label="linked wallet" />
                      <X402SummaryFact value={countX402Facts(x402WalletProfile, "used")} label="recent use" />
                      <X402SummaryFact value={Math.max(countX402Facts(x402WalletProfile, "reviewed"), x402Reviews?.length ?? 0)} label="recent review" />
                      <X402SummaryFact value={countX402Facts(x402WalletProfile, "recognized")} label="recent recognition" />
                    </ul>
                  )}

                  {x402Activity.length > 0 && (
                    <ol className="mt-6 divide-y divide-faint border-y border-faint" aria-label="Recent linked-wallet x402 activity">
                      {x402Activity.map((fact) => (
                        <X402ActivityRow key={fact.id} fact={fact} compact />
                      ))}
                    </ol>
                  )}
                  {x402Reviews && x402Reviews.length > 0 && (
                    <div className="mt-7">
                      <X402ReviewerReviewList items={x402Reviews} />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Stats strap — full-width horizontal row of 4 metrics */}
            <section className="px-6 md:px-10 max-w-6xl mx-auto animate-fade-up" style={{ animationDelay: "100ms" }}>
              <div className="border-y border-faint py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                <Stat
                  label="Recognized"
                  value={formatUsdc(data.profile.totalReceived)}
                />
                <Stat
                  label="Boons received"
                  value={data.boonsReceived.toLocaleString()}
                />
                <Stat label="Sent" value={formatUsdc(data.profile.totalSent)} />
                <Stat
                  label="Last recognized"
                  value={data.profile.lastTipAt ? formatRelative(data.profile.lastTipAt) : "—"}
                />
              </div>
            </section>

            {/* Profile details — full-width ledger */}
            <section
              className="px-6 md:px-10 mt-12 md:mt-16 max-w-6xl mx-auto animate-fade-up"
              style={{ animationDelay: "180ms" }}
            >
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-display tracking-tight text-ink">
                  {isAgentProfile ? "Agent details" : "Profile details"}
                </h2>
                <Link to="/board" className="btn-mono text-xs text-muted hover:text-ink">
                  ← Back to board
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px border border-faint bg-faint rounded-md overflow-hidden">
                <DetailCell
                  label="Linked wallet"
                  value={
                    data.linkedWallet ? (
                      <Link className="text-olive-deep hover:underline" to={`/x402/reviewers/${data.linkedWallet}`}>
                        {shortAddr(data.linkedWallet)}
                      </Link>
                    ) : "unlinked"
                  }
                />
                {isAgentProfile && (
                  <>
                    <DetailCell label="Agent owner" value={shortAddr(agent?.owner)} />
                    <DetailCell label="Agent wallet" value={shortAddr(agent?.agentWallet)} />
                  </>
                )}
                <DetailCell label="Escrowed" value={formatUsdc(data.profile.escrowedAmount)} />
                <DetailCell label="Claimed" value={formatUsdc(data.profile.claimedAmount)} />
              </div>
              {data.note && (
                <p className="mt-4 rounded-md border border-faint bg-paper-deep/60 p-3 text-sm text-muted">
                  {data.note}
                </p>
              )}
            </section>

          </>
        )}
      </main>
      <Footer />
    </>
  );
}

function dedupeReviewerItems(items: X402ReviewerReviewItem[]): X402ReviewerReviewItem[] {
  const unique = new Map<string, X402ReviewerReviewItem>();
  for (const item of items) {
    const key = `${item.routeId}:${item.review.reviewer.wallet.toLowerCase()}:${item.review.evidenceKind}:${item.review.publishedAt}`;
    unique.set(key, item);
  }
  return [...unique.values()].sort((left, right) => right.review.publishedAt - left.review.publishedAt);
}

function profileLookupSupported(scheme: string): scheme is "agent" | "github" | "x" {
  return scheme === "agent" || scheme === "github" || scheme === "x";
}

async function loadIdentityProfile(canonical: ReturnType<typeof canonicalizeHandle>): Promise<PublicIdentityProfile | null> {
  try {
    const response = await fetchPublicProfile(canonical.handle);
    if (response.profile) return response.profile;
  } catch {
    // The Worker enrichment route may not be deployed yet. GitHub's public API
    // supports browser CORS, so keep GitHub profile cards working from the app
    // even while the Worker catches up.
  }
  if (canonical.scheme === "github") {
    return await fetchGithubProfileDirect(canonical.handle, canonical.username);
  }
  return null;
}

async function fetchGithubProfileDirect(handle: string, username: string): Promise<PublicIdentityProfile | null> {
  try {
    const res = await fetch(`https://api.github.com/users/${encodeURIComponent(username)}`, {
      headers: { accept: "application/vnd.github+json" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Record<string, unknown>;
    const login = cleanProfileText(body.login, 80) ?? username;
    return {
      handle,
      provider: "github",
      displayName: cleanProfileText(body.name, 120) ?? `github:${login}`,
      username: login,
      avatarUrl: cleanHttpsUrl(body.avatar_url),
      description: cleanProfileText(body.bio, 280),
      url: cleanHttpsUrl(body.html_url) ?? `https://github.com/${encodeURIComponent(login)}`,
    };
  } catch {
    return null;
  }
}

function cleanProfileText(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  return cleaned ? cleaned.slice(0, max) : null;
}

function cleanHttpsUrl(value: unknown): string | null {
  if (typeof value !== "string" || !/^https:\/\//i.test(value)) return null;
  return value.slice(0, 500);
}

function providerProfileLinkLabel(provider: PublicIdentityProfile["provider"]): string {
  if (provider === "github") return "GitHub profile";
  if (provider === "x") return "X profile";
  return "8004 profile";
}

function sanitizeAgentMetadata(raw: AgentMetadataResponse["metadata"]): AgentMetadataResponse["metadata"] {
  if (!raw) return null;
  const cleanText = (value: string | undefined): string | undefined =>
    typeof value === "string" ? value.replace(/[\u0000-\u001f\u007f]/g, "").slice(0, 500) : undefined;
  const image =
    typeof raw.image === "string" && /^https:\/\//i.test(raw.image)
      ? raw.image.slice(0, 500)
      : undefined;
  return {
    ...(cleanText(raw.name) ? { name: cleanText(raw.name) } : {}),
    ...(cleanText(raw.description) ? { description: cleanText(raw.description) } : {}),
    ...(image ? { image } : {}),
  };
}

function identityActivityFacts(
  profile: X402PublicProfileResponse,
  reviews: X402ReviewerReviewItem[] | null,
): X402ProfileActivity[] {
  return (profile.activity ?? [])
    .filter((fact) => fact.kind !== "reviewed" || !reviews?.some((item) => samePublishedReview(fact, item)))
    .slice(0, 4);
}

function samePublishedReview(fact: X402ProfileActivity, item: X402ReviewerReviewItem): boolean {
  const wallet = baseAddressFromAccountId(fact.accountId);
  return Boolean(
    fact.routeId &&
    wallet &&
    item.routeId === fact.routeId &&
    item.review.reviewer.wallet.toLowerCase() === wallet.toLowerCase() &&
    item.review.evidenceKind === fact.evidenceKind &&
    item.review.createdAt === fact.occurredAt
  );
}

function countX402Facts(profile: X402PublicProfileResponse, kind: X402ProfileActivity["kind"]): number {
  return profile.activity?.filter((fact) => fact.kind === kind).length ?? 0;
}

function X402SummaryFact({ value, label }: { value: number; label: string }) {
  return (
    <li className="btn-mono text-muted">
      <span className="text-ink">{value}</span> {label}{value === 1 ? "" : "s"}
    </li>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="btn-mono text-muted text-xs">{label.toLowerCase()}</p>
      <div className="num text-2xl md:text-3xl text-ink tracking-tight mt-1">
        {value}
      </div>
    </div>
  );
}

function DetailCell({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="bg-paper p-4 md:p-5">
      <p className="btn-mono text-muted text-xs uppercase tracking-wide">
        {label}
      </p>
      <p className="mt-1 num text-base md:text-lg text-ink break-all">
        {value}
      </p>
    </div>
  );
}

function Panel({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="card p-8 text-center">
      <p className="text-ink">{message}</p>
      {sub && <p className="text-sm text-muted mt-2">{sub}</p>}
    </div>
  );
}
